import React, { useEffect, useRef, useState } from 'react';
import { FileOpen, FileUpload } from '@mui/icons-material';
import { Box, Button, FormControl, FormHelperText, IconButton, Input, InputLabel } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import CachedIcon from '@mui/icons-material/Cached';
import { FieldError, FieldErrorsImpl, Merge } from 'react-hook-form';
import { DialogDelete } from 'shared/ui/Dialog';

type UploadFilePropsType = {
  onChange: (...event: any[]) => void;
  textLabel: string;
  helperText: string;
  textButton: string;
  errors?: FieldError | Merge<FieldError, FieldErrorsImpl<any>>;
  disabled?: boolean;
  isClear?: boolean;
  style?: React.CSSProperties;
  hint?: string;
  triggerDialog?: boolean;
  isClearable?: boolean;
};

export const UploadFile = ({
  onChange,
  textLabel,
  helperText,
  textButton,
  errors,
  disabled = false,
  isClear,
  style,
  hint,
  triggerDialog = false,
  isClearable,
}: UploadFilePropsType) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogAction, setDialogAction] = useState<(() => void) | null>(null);

  useEffect(() => {
    if (isClear) {
      setSelectedFile(null);
      onChange(null);
    }
  }, [isClear]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    onChange(file);
    if (file) {
      setSelectedFile(file);
    }

    event.target.value = '';
  };

  const handleFileUpload = () => {
    if (triggerDialog) {
      setOpenDialog(true);
      setDialogAction(() => () => inputRef.current?.click());
    } else {
      inputRef.current?.click();
    }
  };

  const handleRemoveFile = () => {
    if (triggerDialog) {
      setOpenDialog(true);
      setDialogAction(() => confirmFileRemoval);
    } else {
      confirmFileRemoval();
    }
  };

  const confirmFileRemoval = () => {
    onChange(null);
    setSelectedFile(null);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setDialogAction(null);
  };

  const handleDialogConfirm = () => {
    setOpenDialog(false);
    if (dialogAction) {
      dialogAction();
    }
    setDialogAction(null);
  };

  return (
    <Box
      mt={2}
      sx={style}
    >
      <Input
        type="file"
        inputRef={inputRef}
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
      {selectedFile ? (
        <FormControl
          fullWidth
          sx={{}}
        >
          <Box
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              border: `1px solid ${errors ? 'red' : '#ccc'}`,
              borderRadius: '4px',
              padding: '10px',
              position: 'relative',
            }}
          >
            <InputLabel
              sx={{
                position: 'absolute',
                top: '-22px',
                background: '#fff',
                padding: '0 4px',
                left: '-7px',
              }}
              size="small"
            >
              {textLabel}
            </InputLabel>

            <div style={{ display: 'flex', alignItems: 'center' }}>
              <FileOpen style={{ marginRight: '10px' }} /> {selectedFile.name}
            </div>

            <Box>
              <IconButton
                onClick={handleFileUpload}
                size="small"
              >
                <CachedIcon />
              </IconButton>
              {isClearable !== false && (
                <IconButton
                  onClick={handleRemoveFile}
                  size="small"
                >
                  <CancelIcon />
                </IconButton>
              )}
            </Box>
          </Box>
        </FormControl>
      ) : (
        <Button
          variant="contained"
          color={errors && 'error'}
          disabled={disabled}
          startIcon={<FileUpload />}
          onClick={handleFileUpload}
        >
          {textButton}
        </Button>
      )}
      {hint && <FormHelperText>{hint}</FormHelperText>}
      <FormHelperText>{`Рекомендуемый формат ${helperText}`}</FormHelperText>
      {errors && <FormHelperText error>{errors.message?.toString()}</FormHelperText>}
      <DialogDelete
        title="Вы действительно хотите удалить файл? все загруженные данные будут удалены"
        open={openDialog}
        setOpen={setOpenDialog}
        onConfirm={handleDialogConfirm}
        onClose={handleDialogClose}
      />
    </Box>
  );
};
